import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const menuItems = [
  { text: <HomeIcon />, path: '/' },
  { text: 'NOSOTROS', path: '/nosotros' },
  { text: 'TRABAJADORES DE LA LUZ', path: '/trabajadores-de-la-luz' },
  { 
    text: 'ESENIOS', 
    path: '/esenios',
    submenu: [
      { text: 'JESHUA', path: '/esenios-jeshua' },
      { text: 'MARÍA MAGDALENA', path: '/esenios-maria-magdalena' },
      { text: 'MARÍA MADRE', path: '/esenios-maria-madre' },
      { text: 'ABUELA ANA', path: '/esenios-abuela-ana' }
    ]
  },
  {
    text: 'ANGELES Y ARCANGELES',
    path: '/angeles-y-arcangeles',
    submenu: [
      { text: 'ANGELES', path: '/angeles-y-arcangeles/angeles' },
      { text: 'MIGUEL', path: '/angeles-y-arcangeles/miguel' },
      { text: 'METATRON', path: '/angeles-y-arcangeles/metatron' },
      { text: 'GABRIEL', path: '/angeles-y-arcangeles/gabriel' },
      { text: 'RAFAEL', path: '/angeles-y-arcangeles/rafael' },
      { text: 'CHAMUEL', path: '/angeles-y-arcangeles/chamuel' },
      { text: 'JOFIEL', path: '/angeles-y-arcangeles/jofiel' },
      { text: 'URIEL', path: '/angeles-y-arcangeles/uriel' },
      { text: 'ZADQUIEL', path: '/angeles-y-arcangeles/zadquiel' },
    ]
  },
  { text: 'GAIA', path: '/gaia' },
  {
    text: 'REZAR & CANTAR & MEDITAR',
    path: '/musica',
    submenu: [
      { text: 'MOM', path: '/musica-mom' },
      { text: 'MANTRAS', path: '/musica-mantras' },
      { text: 'MEDITATIONS', path: '/musica-meditation' }
    ]
  },
  { text: 'CONTACTO', path: '/contacto' }
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event, menuIndex) => {
    setMenuAnchor(event.currentTarget);
    setOpenSubmenu(menuIndex);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setOpenSubmenu(null);
  };

  const handleMobileSubmenuToggle = (menuIndex) => {
    setOpenSubmenu(openSubmenu === menuIndex ? null : menuIndex);
  };

  const isActive = (path) => location.pathname === path;

  const isSubmenuActive = (submenuItems) => {
    return submenuItems.some(item => location.pathname === item.path);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: '#424a4a',
          boxShadow: 3
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  color: '#fff'
                }}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box 
              sx={{ 
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2
              }}
            >
              {menuItems.map((item, index) => (
                <React.Fragment key={item.text}>
                  {item.submenu ? (
                    <Button 
                      aria-controls={`menu-${index}`}
                      aria-haspopup="true"
                      onClick={(e) => handleMenuOpen(e, index)}
                      sx={{
                        color: isActive(item.path) || isSubmenuActive(item.submenu) ? '#ff5722' : '#fff',
                        fontWeight: isActive(item.path) || isSubmenuActive(item.submenu) ? 'bold' : 'normal',
                        px: 1,
                        py: 1,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        backgroundColor: isActive(item.path) || isSubmenuActive(item.submenu) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        textTransform: 'none',
                        fontSize: { sm: '0.8rem', md: '0.9rem', lg: '1rem' },
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {item.text}
                      {openSubmenu === index ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Button>
                  ) : (
                    <Button 
                      component={Link}
                      to={item.path}
                      sx={{
                        color: isActive(item.path) ? '#ff5722' : '#fff',
                        fontWeight: isActive(item.path) ? 'bold' : 'normal',
                        px: 1,
                        py: 1,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        textTransform: 'none',
                        fontSize: { sm: '0.8rem', md: '0.9rem', lg: '1rem' }
                      }}
                    >
                      {item.text}
                    </Button>
                  )}
                </React.Fragment>
              ))}

              {/* Dropdown menu for Esenios */}
              <Menu
                id={`menu-${openSubmenu}`}
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'button',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: '#424a4a',
                    color: '#fff',
                    minWidth: '200px',
                    mt: 0.5
                  }
                }}
              >
                {openSubmenu !== null && menuItems[openSubmenu]?.submenu?.map((subItem) => (
                  <MenuItem 
                    key={subItem.text} 
                    component={Link}
                    to={subItem.path}
                    onClick={handleMenuClose}
                    sx={{
                      color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                      fontWeight: location.pathname === subItem.path ? 'bold' : 'normal',
                      backgroundColor: location.pathname === subItem.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      fontSize: '0.9rem'
                    }}
                  >
                    {subItem.text}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#424a4a',
            color: '#fff'
          }
        }}
      >
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.text}>
              {item.submenu ? (
                <>
                  <ListItem 
                    button 
                    onClick={() => handleMobileSubmenuToggle(index)}
                    sx={{
                      backgroundColor: isActive(item.path) || isSubmenuActive(item.submenu) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ListItemText 
                      primary={item.text} 
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.9rem',
                          color: isActive(item.path) || isSubmenuActive(item.submenu) ? '#ff5722' : '#fff',
                          fontWeight: isActive(item.path) || isSubmenuActive(item.submenu) ? 'bold' : 'normal',
                        }
                      }}
                    />
                    {openSubmenu === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItem>
                  <Collapse in={openSubmenu === index} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem) => (
                        <ListItem 
                          button 
                          key={subItem.text} 
                          component={Link}
                          to={subItem.path}
                          onClick={handleDrawerToggle}
                          sx={{
                            pl: 4,
                            backgroundColor: location.pathname === subItem.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          <KeyboardArrowRightIcon 
                            fontSize="small" 
                            sx={{ 
                              mr: 1,
                              color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                            }} 
                          />
                          <ListItemText 
                            primary={subItem.text} 
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '0.85rem',
                                color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                                fontWeight: location.pathname === subItem.path ? 'bold' : 'normal',
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem 
                  button 
                  component={Link}
                  to={item.path}
                  onClick={handleDrawerToggle}
                  sx={{
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <ListItemText 
                    primary={item.text} 
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        color: isActive(item.path) ? '#ff5722' : '#fff',
                        fontWeight: isActive(item.path) ? 'bold' : 'normal',
                      }
                    }}
                  />
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;